import {
  ProductClass,
} from '../../graphQL/types/shared';
import { generateStringProductId } from '../../graphQL/Utils';
import {
  hsColorsMap,
  ICPYDatum,
  ILoadable,
  IPYDatum,
  LoadableStatus,
  sitcColorMap,
} from '../../Utils';
import {
  HideExports,
  IAssignSectionOutput,
  IDatumWithSection,
} from './Utils';

interface IKeyInput {
  year: number;
  product_id: number;
}
const getKey = ({year, product_id}: IKeyInput) => `${year}-${product_id}`;

const assign = (
    hideExports: HideExports,
    productClass: ProductClass,
    dataStatus: ILoadable<ICPYDatum[]>,
    worldTradeStatus: ILoadable<IPYDatum[]>,
    high2LowStatus: ILoadable<Record<string, number>>,
  ): ILoadable<IAssignSectionOutput> => {

  let result: ILoadable<IAssignSectionOutput>;
  if (dataStatus.status === LoadableStatus.Initial ||
      high2LowStatus.status === LoadableStatus.Initial ||
      worldTradeStatus.status === LoadableStatus.Initial) {
    result = {status: LoadableStatus.Initial};
  } else if (dataStatus.status === LoadableStatus.Loading ||
              high2LowStatus.status === LoadableStatus.Loading ||
              worldTradeStatus.status === LoadableStatus.Loading) {
    result = {status: LoadableStatus.Loading};
  } else if (dataStatus.status === LoadableStatus.NotPresent ||
              high2LowStatus.status === LoadableStatus.NotPresent ||
              worldTradeStatus.status === LoadableStatus.NotPresent) {
    result = {status: LoadableStatus.NotPresent};
  } else {
    const {data} = dataStatus;
    const {data: high2Low} = high2LowStatus;
    const {data: worldTradeData} = worldTradeStatus;

    const worldTradePairs = worldTradeData.map(
      datum => ([getKey(datum), datum] as [string, IPYDatum]),
    );
    const worldTradeMap = new Map(worldTradePairs);
    const colorMap = (productClass === ProductClass.HS) ? hsColorsMap : sitcColorMap;

    const withSection: IDatumWithSection[] = [];
    const dataLength = data.length;
    for (let i = 0; i < dataLength; i += 1) {
      const datum = data[i];
      const {
        export_rca, distance, cog,
        product_id: numericProductID,
        year,
      } = datum;
      const stringProductID = generateStringProductId({productClass, id: numericProductID});
      const {export_value: exportValueFromWorldTrade, pci} = worldTradeMap.get(getKey(datum))!;
      if (export_rca !== null && cog !== null && distance !== null && pci !== null) {
        const numericSectionID = high2Low[datum.product_id];
        const stringSectionID = generateStringProductId({productClass, id: numericSectionID});
        const color = colorMap.get(numericSectionID)!;
        const toBeAdded: IDatumWithSection = {
          product_id: stringProductID,
          export_rca, year,
          distance, cog,
          section: stringSectionID,
          color,
          worldTrade: exportValueFromWorldTrade,
          pci,
        };
        withSection.push(toBeAdded);
      }
    }

    // Determine all limits for data (after excluding exported products
    // if requested):
    let dataForLimits: IDatumWithSection[];
    if (hideExports === HideExports.On) {
      dataForLimits = withSection.filter(({export_rca}) => export_rca < 1);
    } else {
      dataForLimits = withSection;
    }

    const allWorldTrades = dataForLimits.map(({worldTrade}) => worldTrade);
    const allOpportunityGains = dataForLimits.map(({cog}) => cog);
    const allDistances = dataForLimits.map(({distance}) => distance);
    const allPCIs = dataForLimits.map(({pci}) => pci);
    result = {
      status: LoadableStatus.Present,
      data: {
        nodes: withSection,
        maxWorldTrade: Math.max(...allWorldTrades),
        minWorldTrade: Math.min(...allWorldTrades),
        maxOpportunityGain: Math.max(...allOpportunityGains),
        minOpportunityGain: Math.min(...allOpportunityGains),
        maxDistance: Math.max(...allDistances),
        minDistance: Math.min(...allDistances),
        maxComplexity: Math.max(...allPCIs),
        minComplexity: Math.min(...allPCIs),
      },
    };
  }
  return result;
};

export default assign;
